import type { ReactNode } from "react";
import { cn } from "@/shared/lib/cn";
import marcaLogo from "@/assets/marca.svg";

export interface AuthCardProps {
  /** Título grande bajo el logotipo. */
  title: ReactNode;
  /** Línea de apoyo bajo el título. */
  subtitle?: ReactNode;
  /** El contenido — normalmente el formulario. */
  children: ReactNode;
  /** El pie de la card: "¿Ya tienes cuenta? Entra". */
  footer?: ReactNode;
  /**
   * Aprieta el encabezado y el relleno. Para formularios largos —el
   * registro— donde cada píxel que gana la cabecera es un píxel que
   * empuja el botón de enviar fuera de la pantalla.
   */
  compact?: boolean;
  /** Texto accesible del logotipo. */
  logoLabel?: string;
  className?: string;
}

/**
 * La card de vidrio de las pantallas de acceso.
 *
 * Login y registro usan EXACTAMENTE esta card: si el lenguaje visual
 * cambia, cambia en un solo archivo y las dos pantallas lo heredan.
 *
 * ── El brillo diagonal se retiró (26-ago) ───────────────────────────
 * Llevaba encima una capa con `bg-gradient-to-br from-white/50 via-
 * transparent to-ds-mist/50`. Era un degradado decorativo sobre una
 * superficie, y esos se fueron del sistema entero. Lo que hacía de
 * verdad era subir el blanco por una esquina; eso ahora lo hace el propio
 * fondo, que pasa de `bg-white/35` a `bg-white/45` —dentro del rango que
 * recorría la diagonal— y es igual en toda la card. El volumen sigue
 * viniendo del desenfoque de fondo, del borde y de la sombra.
 *
 * ══════════════════════════════════════════════════════════════════════
 * LO QUE SALIÓ AL MEDIR, Y QUE NO LO ARREGLA ESTE CAMBIO
 *
 * Medido sobre las capturas REALES del login a 1440 —el fondo inmediato
 * de cada texto, en dos franjas de 6 px justo encima y justo debajo de
 * sus glifos—, no sobre el color teórico de la card:
 *
 *                              antes (diagonal)   ahora (plano)
 *   «¡Bienvenido!»  ds-ink          5.3 : 1          5.2 : 1
 *   «Correo»        ds-ink-3        4.4 : 1          3.6 : 1
 *   «¿Olvidaste…?»  ds-ink-3        3.2 : 1          4.0 : 1
 *   pie de la card  ds-ink-3        3.2 : 1          3.1 : 1
 *
 * El texto pequeño de esta card NO llega a 4.5 : 1, y NO llegaba antes
 * tampoco. La causa no es el brillo: es que `login-hero.webp` tiene zonas
 * de negro puro justo detrás de la card y un vidrio al 35–45 % no las
 * tapa. Quitar la diagonal solo redistribuye —sube donde ella oscurecía,
 * baja donde ella aclaraba— y deja el peor punto donde estaba.
 *
 * Para que el peor texto pasara haría falta subir el vidrio a ~57 %, y a
 * esa opacidad la card deja de leerse como vidrio sobre una foto y pasa a
 * ser un panel casi opaco. Eso ya no es aplanar un degradado: es cambiar
 * la pieza que Anahi aprobó, así que va como aviso y no como parche.
 *
 * (La otra salida, y probablemente la buena, es oscurecer o desenfocar la
 * FOTO bajo la card en vez de tapar la card: el problema es el fondo.)
 * ══════════════════════════════════════════════════════════════════════
 *
 * La misma fórmula, sin card, es `GlassPanel`; los dos cambiaron a la vez
 * a propósito, que para eso son la misma superficie.
 *
 * El logotipo se pinta con máscara CSS sobre `marca.svg` y no con un
 * `<img>`: así el color sale de `--violet-pop` y sigue a la marca si la
 * paleta cambia, sin tener que reexportar el SVG. Por eso es un `<div>`
 * vacío con `role="img"` y `aria-label` — no hay contenido que leer.
 */
export function AuthCard({
  title,
  subtitle,
  children,
  footer,
  compact = false,
  logoLabel = "It's The Best",
  className,
}: AuthCardProps) {
  return (
    <div
      className={cn(
        "relative rounded-3xl border border-ds-border bg-white/45 backdrop-blur-2xl",
        "shadow-[0_25px_60px_rgba(0,0,0,0.08)]",
        className,
      )}
    >
      <div className={cn("relative", compact ? "p-6 sm:p-7" : "p-7 sm:p-8")}>
        <div
          className={cn(
            "flex flex-col items-center text-center",
            compact ? "mb-5" : "mb-6",
          )}
        >
          <div
            role="img"
            aria-label={logoLabel}
            className={cn(compact ? "mb-2.5 h-10 w-[150px]" : "mb-3.5 h-14 w-[200px]")}
            style={{
              backgroundColor: "var(--violet-pop)",
              WebkitMaskImage: `url(${marcaLogo})`,
              maskImage: `url(${marcaLogo})`,
              WebkitMaskRepeat: "no-repeat",
              maskRepeat: "no-repeat",
              WebkitMaskPosition: "center",
              maskPosition: "center",
              WebkitMaskSize: "contain",
              maskSize: "contain",
            }}
          />
          <h1
            className={cn(
              "font-light tracking-tight text-ds-ink",
              compact ? "text-xl" : "text-2xl",
            )}
          >
            {title}
          </h1>
          {subtitle && (
            <p className="mt-1.5 max-w-xs text-[13px] leading-relaxed text-ds-ink-3">
              {subtitle}
            </p>
          )}
        </div>

        {children}

        {footer && (
          <div
            className={cn(
              "text-center text-sm text-ds-ink-3",
              compact ? "mt-5" : "mt-6",
            )}
          >
            {footer}
          </div>
        )}
      </div>
    </div>
  );
}
